import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { orderLineDisplayLabel, orderItemThumbnailSrc } from "../utils/orderLineMedia";
import { formatMoney } from "../utils/money";
import { statusLabel } from "./orderKit";
import { t } from "../i18n";

const MERCATOR_MAX_LAT = 85.05112878;
const SAFE_MIN_ZOOM = 5;
const SAFE_MAX_ZOOM = 19;

function safeCoord(lat, lng) {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lng >= -180 &&
    lng <= 180 &&
    lat >= -MERCATOR_MAX_LAT &&
    lat <= MERCATOR_MAX_LAT
  );
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    try {
      const b = L.latLngBounds(points);
      if (!b.isValid()) return;
      map.fitBounds(b, { padding: [56, 56], maxZoom: 15 });
    } catch (_) {}
  }, [map, points]);
  return null;
}

function orderPinIcon(order, selected) {
  let cls = "rom-ready-pin";
  if (order.directDeliveryOnly) cls += " rom-ready-pin-direct";
  if (selected) cls += " rom-ready-pin-selected";
  return L.divIcon({
    className: "rom-ready-pin-root",
    html: `<div class="${cls}" title="${t("order.title", { id: order.id })}">${order.id}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

function OrderPinMarker({ order, selected, onToggleSelect, onShowDetail }) {
  const timerRef = useRef(null);
  const longFiredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startLongPress = useCallback(() => {
    clearTimer();
    longFiredRef.current = false;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      longFiredRef.current = true;
      onShowDetail(order);
    }, 520);
  }, [clearTimer, onShowDetail, order]);

  const eventHandlers = useMemo(
    () => ({
      contextmenu(e) {
        L.DomEvent.preventDefault(e.originalEvent);
        clearTimer();
        onShowDetail(order);
      },
      mousedown() {
        startLongPress();
      },
      mouseup() {
        clearTimer();
      },
      mouseleave() {
        clearTimer();
      },
      touchstart() {
        startLongPress();
      },
      touchend() {
        clearTimer();
      },
      touchcancel() {
        clearTimer();
      },
      click(e) {
        if (longFiredRef.current) {
          longFiredRef.current = false;
          L.DomEvent.stopPropagation(e.originalEvent);
          return;
        }
        L.DomEvent.stopPropagation(e.originalEvent);
        onToggleSelect(order.id);
      },
    }),
    [clearTimer, onShowDetail, onToggleSelect, order, startLongPress]
  );

  const lat = order.deliveryLatitude;
  const lng = order.deliveryLongitude;
  if (!safeCoord(lat, lng)) return null;

  return (
    <Marker position={[lat, lng]} icon={orderPinIcon(order, selected)} eventHandlers={eventHandlers} />
  );
}

function fmtCreated(iso) {
  if (!iso) return "N/A";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "N/A";
    return d.toLocaleString();
  } catch {
    return "N/A";
  }
}

/**
 * Full-screen style map: tap pin = toggle selection; long-press or right-click = delivery detail (odos_merchant parity).
 */
export function ReadyOrdersMap({ orders, selectedIds, onChangeSelection, merchantOffice, onClose }) {
  const [detailOrder, setDetailOrder] = useState(null);
  const [userPos, setUserPos] = useState(null);

  const onMap = useMemo(
    () => (orders || []).filter((o) => safeCoord(o.deliveryLatitude, o.deliveryLongitude)),
    [orders]
  );

  const missingCount = (orders || []).length - onMap.length;

  const fitPoints = useMemo(() => {
    const pts = onMap.map((o) => [o.deliveryLatitude, o.deliveryLongitude]);
    if (merchantOffice && safeCoord(merchantOffice.lat, merchantOffice.lng)) {
      pts.push([merchantOffice.lat, merchantOffice.lng]);
    }
    if (userPos && safeCoord(userPos.lat, userPos.lng)) {
      pts.push([userPos.lat, userPos.lng]);
    }
    return pts;
  }, [merchantOffice, onMap, userPos]);

  const initialCenter = useMemo(() => {
    if (onMap.length > 0) return [onMap[0].deliveryLatitude, onMap[0].deliveryLongitude];
    if (merchantOffice && safeCoord(merchantOffice.lat, merchantOffice.lng)) return [merchantOffice.lat, merchantOffice.lng];
    return [9.03, 38.76];
  }, [merchantOffice, onMap]);

  const toggleSelect = useCallback(
    (orderId) => {
      const order = orders.find((o) => o.id === orderId);
      if (!order) return;
      const next = new Set(selectedIds);
      if (next.has(orderId)) {
        next.delete(orderId);
        onChangeSelection(next);
        return;
      }
      if (order.directDeliveryOnly) {
        onChangeSelection(new Set([orderId]));
        return;
      }
      for (const id of [...next]) {
        const o = orders.find((x) => x.id === id);
        if (o?.directDeliveryOnly) next.delete(id);
      }
      next.add(orderId);
      onChangeSelection(next);
    },
    [onChangeSelection, orders, selectedIds]
  );

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        if (safeCoord(lat, lng)) setUserPos({ lat, lng });
      },
      () => {},
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 12_000 }
    );
  }, []);

  function recenterUser() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        if (safeCoord(lat, lng)) setUserPos({ lat, lng });
      },
      () => {},
      { enableHighAccuracy: false, timeout: 12_000 }
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ready-map-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 5000,
        background: "var(--bg-page, #f8f6fc)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        className="card"
        style={{
          margin: 0,
          borderRadius: 0,
          borderLeft: "none",
          borderRight: "none",
          borderTop: "none",
          padding: "12px 16px",
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h2 id="ready-map-title" style={{ margin: 0, fontSize: 18 }}>
          {t("readymap.title")}
        </h2>
        <div className="row wrap" style={{ gap: 8 }}>
          <button type="button" className="secondary" onClick={recenterUser}>
            {t("readymap.my_location")}
          </button>
          <button type="button" className="primary" onClick={onClose}>
            {t("common.done")}
          </button>
        </div>
      </div>

      {missingCount > 0 ? (
        <div
          style={{
            padding: "8px 16px",
            background: "#faf1df",
            color: "var(--text-primary, #1b1530)",
            fontSize: 13,
          }}
        >
          {missingCount} order(s) have no map pin - select them in the list below.
        </div>
      ) : null}

      <div className="rom-map-stage">
        <MapContainer
          center={initialCenter}
          zoom={12}
          minZoom={SAFE_MIN_ZOOM}
          maxZoom={SAFE_MAX_ZOOM}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={fitPoints} />

          {merchantOffice && safeCoord(merchantOffice.lat, merchantOffice.lng) ? (
            <CircleMarker
              center={[merchantOffice.lat, merchantOffice.lng]}
              radius={10}
              pathOptions={{ color: "#1b1530", weight: 2, fillColor: "#2e7d32", fillOpacity: 1 }}
              eventHandlers={{
                click(e) {
                  L.DomEvent.stopPropagation(e.originalEvent);
                },
              }}
            />
          ) : null}

          {userPos && safeCoord(userPos.lat, userPos.lng) ? (
            <CircleMarker
              center={[userPos.lat, userPos.lng]}
              radius={9}
              pathOptions={{ color: "#1b1530", weight: 2, fillColor: "#1976d2", fillOpacity: 1 }}
              eventHandlers={{
                click(e) {
                  L.DomEvent.stopPropagation(e.originalEvent);
                },
              }}
            />
          ) : null}

          {onMap.map((o) => (
            <OrderPinMarker
              key={o.id}
              order={o}
              selected={selectedIds.has(o.id)}
              onToggleSelect={toggleSelect}
              onShowDetail={setDetailOrder}
            />
          ))}
        </MapContainer>

        <div className="card rom-map-legend">
          <div className="rom-map-legend-title">{t("readymap.legend")}</div>
          <div>{t("readymap.tap_to_select")}</div>
          <div className="rom-map-legend-hint">{t("readymap.long_press")}</div>
          <div className="rom-map-legend-selected">{t("readymap.n_selected", { n: selectedIds.size })}</div>
          {merchantOffice ? (
            <div className="rom-map-legend-row">
              <span className="rom-map-legend-dot rom-map-legend-dot-store">●</span>
              <span>{t("readymap.store_pickup")}</span>
            </div>
          ) : null}
          {userPos ? (
            <div className="rom-map-legend-row">
              <span className="rom-map-legend-dot rom-map-legend-dot-user">●</span>
              <span>{t("readymap.your_location")}</span>
            </div>
          ) : null}
          <div className="rom-map-legend-row">
            <span className="rom-map-legend-swatch rom-map-legend-swatch-pin" />
            <span>{t("readymap.order_stop")}</span>
          </div>
        </div>
      </div>

      {detailOrder ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 5100,
            background: "rgba(46, 33, 84, 0.14)",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) setDetailOrder(null);
          }}
          onKeyDown={(ev) => {
            if (ev.key === "Escape") setDetailOrder(null);
          }}
        >
          <div
            className="card rom-detail-sheet"
            style={{
              width: "100%",
              maxWidth: 520,
              maxHeight: "82vh",
              overflow: "auto",
              margin: 0,
              borderRadius: "14px 14px 0 0",
            }}
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <h3 style={{ margin: 0 }}>{t("order.title", { id: detailOrder.id })}</h3>
              <button type="button" className="secondary" onClick={() => setDetailOrder(null)}>
                {t("common.close")}
              </button>
            </div>
            <p style={{ margin: "8px 0", fontWeight: 600 }}>
              {t("common.amount_etb", { amount: formatMoney(detailOrder.total) })} · {statusLabel(detailOrder.status)}
            </p>
            <p className="muted" style={{ margin: "0 0 12px", fontSize: 13 }}>
              {t("readymap.placed", { when: fmtCreated(detailOrder.createdAt) })}
            </p>
            {detailOrder.directDeliveryOnly ? (
              <span className="rom-direct-chip">{t("order.direct_delivery")}</span>
            ) : null}

            <h4 style={{ margin: "16px 0 8px", fontSize: 14 }}>{t("readymap.delivery")}</h4>
            <p style={{ margin: "0 0 8px" }}>
              {detailOrder.deliveryAddress?.trim() ? detailOrder.deliveryAddress.trim() : t("readymap.no_address")}
            </p>
            {detailOrder.clientPhone?.trim() ? (
              <p style={{ margin: "0 0 8px", fontSize: 14 }}>
                {t("readymap.phone")}{" "}
                <a href={`tel:${String(detailOrder.clientPhone).replace(/\s+/g, "")}`}>{detailOrder.clientPhone.trim()}</a>
              </p>
            ) : null}
            {safeCoord(detailOrder.deliveryLatitude, detailOrder.deliveryLongitude) ? (
              <p className="muted" style={{ margin: 0, fontSize: 12, fontFamily: "monospace" }}>
                {t("readymap.pin", {
                  coords: `${Number(detailOrder.deliveryLatitude).toFixed(5)}, ${Number(detailOrder.deliveryLongitude).toFixed(5)}`,
                })}
              </p>
            ) : null}

            {(detailOrder.items || []).length > 0 ? (
              <>
                <h4 style={{ margin: "16px 0 8px", fontSize: 14 }}>{t("readymap.items")}</h4>
                <ul className="rom-detail-items">
                  {(detailOrder.items || []).map((item) => (
                    <li key={item.id} className="rom-detail-item-row">
                      <img
                        className="rom-detail-thumb"
                        src={orderItemThumbnailSrc(item.imageUrl)}
                        alt=""
                        loading="lazy"
                      />
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {item.quantity}× {orderLineDisplayLabel(item)}
                        </div>
                        {item.sku && orderLineDisplayLabel(item) !== item.sku ? (
                          <div className="muted" style={{ fontSize: 12 }}>
                            {item.sku}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
